import { GMT_DISTANCE_ROWING, GMT_DISTANCE_PADDLING, LUDUM_SPORTS } from '@/constants';
const { ROWING } = LUDUM_SPORTS;
import { SECONDS_IN_HOUR, METERS_IN_KILOMETER, SECONDS_IN_MINUTE } from '@/constants/global';
import {
    isBikeRelatedSession,
    isErgoRelatedSession,
    isErgoSession,
    isIndoorCyclingRelatedSession,
    isIndoorPaddlingSession,
    isOtherRelatedSession,
    isPaddlingSession,
    isRowingSession,
    isRunRelatedSession,
    isScRelatedSession,
    isStandUpPaddleSession,
    isSwimRelatedSession,
    isWaterSession
} from '@/helpers/sessionHelper';
import { round } from '@/util/numberFormatter';
import get from 'get-object-path';

export function sessionHasResults(session) {
    return !!get(session, 'statistics.data.has_results');
}

export function sessionHasHrData(session) {
    return !!get(session, 'statistics.data.has_hr_data');
}

export function sessionHasGpsData(session) {
    return !!get(session, 'statistics.data.has_gps_data');
}

export function calculateResultsTotalTonnage(userResults) {
    const pieces = userResults
        .filter(result => isScRelatedSession(get(result, 'sport.id')))
        .map(result => get(result, 'pieces.data') || [])
        .flat();

    let totalTonnage = 0;
    pieces.forEach(piece => {
        const segments = get(piece, 'segments.data') || [];
        segments.forEach(segment => {
            totalTonnage += segment.reps_count * segment.exercise_weight;
        });
    });

    return totalTonnage;
}

export function isAthleteSelected(selectedAthletes, athleteId) {
    const isIn = selectedAthletes.includes(athleteId);
    return !selectedAthletes.length || isIn;
}

export function isCrewSelected(selectedCrews, id, field = 'id') {
    const crewInFilter = selectedCrews.find(crew => crew[field] === id);
    return !selectedCrews.length || !!crewInFilter;
}

export function assignResultsToUser(user) {
    // Transform athlete object so it matches 'sport_user_results' format
    if (!user.overall_results && get(user, 'sport.data[0].overall_results')) {
        Object.assign(user, { overall_results: user.sport.data[0].overall_results });
    }

    if (!user.pieces && get(user, 'sport.data[0].pieces')) {
        Object.assign(user, { pieces: user.sport.data[0].pieces });
    }
}

export function calculateValue(position, type, sportId) {
    const value = parseFloat(position[type]);
    if (type === 'speed') {
        return calculateSpeed(value, sportId);
    } else if (type === 'pace') {
        return calculatePace(value, sportId);
    } else {
        return round(value);
    }
}

export function calculateSpeed(argSpeed, sportId) {
    const speed = Number(argSpeed);
    if (
        isRunRelatedSession(sportId) ||
        isBikeRelatedSession(sportId) ||
        isIndoorCyclingRelatedSession(sportId) ||
        isPaddlingSession(sportId)
    ) {
        return speed ? round((speed * SECONDS_IN_HOUR) / METERS_IN_KILOMETER) : 0;
    }
    return round(speed) || 0;
}

export function calculatePaceGraph(argSpeed, sportId) {
    return calculatePace(argSpeed, sportId, yAxisPaceMax(sportId) || 0);
}

export function calculatePace(argSpeed, sportId, emptyValue = 0) {
    const speed = Number(argSpeed);
    if (isErgoRelatedSession(sportId) || isWaterSession(sportId)) {
        return speed ? round(splitLength(sportId) / speed) : emptyValue;
    }
    if (
        isRunRelatedSession(sportId) ||
        isBikeRelatedSession(sportId) ||
        isIndoorCyclingRelatedSession(sportId) ||
        isOtherRelatedSession(sportId)
    ) {
        return speed ? round(1000 / speed) : emptyValue;
    }
    if (isSwimRelatedSession(sportId)) {
        return speed ? round(100 / speed) : emptyValue;
    }
    return round(speed);
}

export function kalmanCoefficients(sportId) {
    if (isErgoRelatedSession(sportId) || isRowingSession(sportId)) {
        return 0.01;
    }
    if (
        isRunRelatedSession(sportId) ||
        isBikeRelatedSession(sportId) ||
        isIndoorCyclingRelatedSession(sportId) ||
        isPaddlingSession(sportId)
    ) {
        return 0.5;
    }
    return 1;
}

export function unitsOfMeasurementSpeed(sportId) {
    if (
        isRunRelatedSession(sportId) ||
        isBikeRelatedSession(sportId) ||
        isIndoorCyclingRelatedSession(sportId) ||
        isPaddlingSession(sportId)
    ) {
        return 'km/h';
    }
    return 'm/s';
}

export function unitsOfMeasurementPace(sportId) {
    if (isErgoRelatedSession(sportId) || isWaterSession(sportId)) {
        const split = splitLength(sportId);
        return split === 500 ? 'min/500m' : 'min/km';
    }
    if (
        isRunRelatedSession(sportId) ||
        isBikeRelatedSession(sportId) ||
        isIndoorCyclingRelatedSession(sportId) ||
        isOtherRelatedSession(sportId)
    ) {
        return 'min/km';
    }
    if (isSwimRelatedSession(sportId)) {
        return 'sec/100m';
    }
    return 'm/s';
}

export function splitLength(sportId) {
    if (isSwimRelatedSession(sportId)) {
        return 100;
    }
    if (
        isErgoSession(sportId) ||
        isIndoorPaddlingSession(sportId) ||
        isWaterSession(sportId) ||
        isStandUpPaddleSession(sportId)
    ) {
        return 500;
    }
    return 1000;
}

export function gmtDistance(sportId = ROWING) {
    return isPaddlingSession(sportId) ? GMT_DISTANCE_PADDLING : GMT_DISTANCE_ROWING;
}

export function gmtPaceDivider(sportId = ROWING) {
    return gmtDistance(sportId) / splitLength(sportId);
}

export function yAxisPaceMax(sportId) {
    if (isBikeRelatedSession(sportId) || isIndoorCyclingRelatedSession(sportId)) {
        return 12 * SECONDS_IN_MINUTE;
    }
    if (isErgoRelatedSession(sportId)) {
        return 3.5 * SECONDS_IN_MINUTE;
    }
    if (isRunRelatedSession(sportId)) {
        return 30 * SECONDS_IN_MINUTE;
    }
    if (isSwimRelatedSession(sportId)) {
        return 36;
    }
    if (isWaterSession(sportId)) {
        return 5 * SECONDS_IN_MINUTE;
    }
    return undefined;
}

export function yAxisSpeedMax(sportId) {
    if (isPaddlingSession(sportId)) {
        return 5;
    }
    return undefined;
}

export function yAxisPowerMax(sportId) {
    if (isPaddlingSession(sportId)) {
        return 1000;
    }
    return undefined;
}
